import React, { useEffect, useState } from "react";
import api, { csrfHeaders } from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { Calendar as CalIcon, FileText, Check, X, AlertOctagon, Camera } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });


function AvatarSection() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const submit = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image trop volumineuse (max 5 Mo)");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/avatar`, {
        method: "POST", body: fd, credentials: "include",
        headers: csrfHeaders(),
      });
      if (!res.ok) throw new Error();
      toast.success("Photo de profil mise à jour");
      if (refresh) await refresh();
    } catch { toast.error("Échec du téléversement"); }
    finally { setBusy(false); }
  };
  const src = user?.picture
    ? (user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "MNS")}&background=0369A1&color=fff&size=200`;
  return (
    <div className="flex items-center gap-4 mb-6 pb-6 border-b" data-testid="avatar-section">
      <img src={src} alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-sky-100" />
      <div className="flex-1">
        <Label className="block text-sm">Photo de profil</Label>
        <p className="text-xs text-slate-500 mb-1">JPG/PNG/WebP — 5 Mo max. Visible par les clients sur votre fiche.</p>
        <label className="inline-flex items-center gap-1 cursor-pointer text-sm text-sky-700 font-medium hover:underline">
          <Camera className="w-4 h-4" />{busy ? "Envoi..." : "Modifier la photo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => submit(e.target.files?.[0])} data-testid="avatar-input" />
        </label>
      </div>
    </div>
  );
}

export function ClientProfile() {
  const [form, setForm] = useState({ first_name: "", last_name: "", level: "debutant", goals: "", special_profile: "" });
  useEffect(() => { api.get("/profile/client").then((r) => r.data?.first_name && setForm({ ...form, ...r.data })); /* eslint-disable-next-line */ }, []);
  const save = async (e) => { e.preventDefault(); await api.put("/profile/client", form); toast.success("Profil enregistré"); };
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="card-soft p-8">
        <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Mon profil</h1>
        <form onSubmit={save} className="space-y-4" data-testid="client-profile-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label>Nom</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div>
            <Label>Niveau</Label>
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="debutant">Débutant</SelectItem>
                <SelectItem value="intermediaire">Intermédiaire</SelectItem>
                <SelectItem value="avance">Avancé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Objectifs</Label><Textarea required value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="Ex: Apprendre la brasse, perfectionnement..." /></div>
          <div><Label>Profil spécifique (optionnel)</Label><Input value={form.special_profile} onChange={(e) => setForm({ ...form, special_profile: e.target.value })} placeholder="Ex: Peur de l'eau, handicap, asthme..." /></div>
          <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="save-client-profile">Enregistrer</Button>
        </form>
      </div>
    </div>
  );
}

export function MnsProfileEdit() {
  const [pools, setPools] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", bio: "", hourly_rate: 35,
    max_students_per_slot: 1, accepts_mixed_levels: false,
    pool_entry_fee_at_reception: false, attached_pool_ids: [],
    diplomas: [{ _uid: "dip-1", name: "BPJEPS AAN", issued_at: "", valid_until: "" }],
    certified_engagement: false,
    siret: "", vat_subject: false, vat_rate: 20.0,
  });
  const [completeness, setCompleteness] = useState(null);
  const [insurance, setInsurance] = useState([]);
  const [insForm, setInsForm] = useState({ valid_until: "", file: null });
  const [insBusy, setInsBusy] = useState(false);
  useEffect(() => {
    api.get("/pools").then((r) => setPools(r.data));
    api.get("/profile/mns").then((r) => {
      if (r.data) {
        setCompleteness(r.data.completeness || null);
        if (r.data.first_name) setForm((f) => ({ ...f, ...r.data }));
      }
    });
    api.get("/insurance/me").then((r) => setInsurance(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  const togglePool = (id) => {
    const has = form.attached_pool_ids.includes(id);
    setForm({ ...form, attached_pool_ids: has ? form.attached_pool_ids.filter((x) => x !== id) : [...form.attached_pool_ids, id] });
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.certified_engagement) return toast.error("Vous devez certifier vos diplômes valides.");
    if (!form.siret || form.siret.length < 9) return toast.error("Le numéro SIRET est obligatoire (14 chiffres).");
    await api.put("/profile/mns", form);
    const fresh = await api.get("/profile/mns");
    setCompleteness(fresh.data.completeness || null);
    toast.success("Profil MNS enregistré");
  };

  const submitInsurance = async (e) => {
    e.preventDefault();
    if (!insForm.file || !insForm.valid_until) return toast.error("Champs incomplets");
    setInsBusy(true);
    try {
      const fd = new FormData(); fd.append("file", insForm.file);
      const params = new URLSearchParams({ valid_until: insForm.valid_until }).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insurance/upload?${params}`, {
        method: "POST", body: fd,
        headers: csrfHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error("upload failed");
      toast.success("Attestation d'assurance téléversée");
      setInsForm({ valid_until: "", file: null });
      const list = await api.get("/insurance/me"); setInsurance(list.data);
      const fresh = await api.get("/profile/mns"); setCompleteness(fresh.data.completeness || null);
    } catch { toast.error("Échec du téléversement"); }
    finally { setInsBusy(false); }
  };

  const delInsurance = async (id) => {
    await api.delete(`/insurance/${id}`);
    const list = await api.get("/insurance/me"); setInsurance(list.data);
    const fresh = await api.get("/profile/mns"); setCompleteness(fresh.data.completeness || null);
  };
  const addDip = () => setForm({ ...form, diplomas: [...form.diplomas, { _uid: `dip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: "", issued_at: "", valid_until: "" }] });
  const updDip = (i, k, v) => {
    const list = [...form.diplomas]; list[i] = { ...list[i], [k]: v }; setForm({ ...form, diplomas: list });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="card-soft p-8">
        <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Mon profil de MNS</h1>
        <AvatarSection />
        <form onSubmit={save} className="space-y-4" data-testid="mns-profile-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label>Nom</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div><Label>Présentation</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Tarif horaire (€)</Label><Input type="number" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) })} /></div>
            <div><Label>Élèves max / créneau</Label><Input type="number" min="1" value={form.max_students_per_slot} onChange={(e) => setForm({ ...form, max_students_per_slot: parseInt(e.target.value) })} /></div>
          </div>
          <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl"><Label>Accepter niveaux mixtes</Label><Switch checked={form.accepts_mixed_levels} onCheckedChange={(v) => setForm({ ...form, accepts_mixed_levels: v })} /></div>
          <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl"><Label>Entrée piscine à régler à l'accueil</Label><Switch checked={form.pool_entry_fee_at_reception} onCheckedChange={(v) => setForm({ ...form, pool_entry_fee_at_reception: v })} /></div>

          <div>
            <Label>Établissements rattachés</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-60 overflow-auto border rounded-xl p-3">
              {pools.map((p) => (
                <label key={p.pool_id} className={`text-sm p-2 rounded-lg cursor-pointer ${form.attached_pool_ids.includes(p.pool_id) ? "bg-sky-100 text-sky-800" : "bg-white"}`}>
                  <input type="checkbox" className="mr-2" checked={form.attached_pool_ids.includes(p.pool_id)} onChange={() => togglePool(p.pool_id)} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>

          <div className="border-t pt-4">
            <h2 className="font-bold mb-3 text-slate-900" style={{ fontFamily: "Outfit" }}>Informations administratives</h2>
            {completeness && !completeness.complete && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-sm text-amber-800" data-testid="profile-incomplete">
                <strong>⚠ Profil incomplet</strong> — vous ne pouvez pas encore publier de créneaux.
                Champs manquants : {completeness.missing.join(", ")}.
              </div>
            )}
            {completeness?.complete && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3 text-sm text-emerald-800" data-testid="profile-complete">
                ✅ Votre profil est complet et validé.
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Numéro SIRET <span className="text-red-500">*</span></Label>
                <Input required value={form.siret || ""} onChange={(e) => setForm({ ...form, siret: e.target.value.replace(/\s/g, "") })} placeholder="14 chiffres" maxLength={14} data-testid="siret-input" />
              </div>
              <div>
                <Label>Soumis à la TVA ?</Label>
                <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl mt-1">
                  <Switch checked={!!form.vat_subject} onCheckedChange={(v) => setForm({ ...form, vat_subject: v })} data-testid="vat-toggle" />
                  <span className="text-sm">{form.vat_subject ? "Oui" : "Non — franchise en base"}</span>
                </div>
              </div>
              {form.vat_subject && (
                <div>
                  <Label>Taux de TVA (%)</Label>
                  <Input type="number" step="0.1" min="0" value={form.vat_rate || 20} onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) })} data-testid="vat-rate-input" />
                </div>
              )}
            </div>
          </div>

          {/* Iter42 — Documents officiels regroupés en bloc repliable pour alléger
              le formulaire de création. La validation côté serveur reste inchangée. */}
          <details className="border-t pt-4 group" data-testid="docs-collapsible" open={completeness && !completeness.complete && (insurance.length === 0 || !form.diplomas?.[0]?.name)}>
            <summary className="cursor-pointer list-none flex items-center justify-between gap-3 select-none">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>📄 Documents officiels</span>
                <span className="text-xs text-slate-500">(diplômes + attestation d'assurance)</span>
              </div>
              <div className="flex items-center gap-2">
                {completeness && !completeness.complete && (insurance.length === 0 || !form.diplomas?.[0]?.name) && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">À compléter</span>
                )}
                {insurance.length > 0 && form.diplomas?.[0]?.name && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">✓ Renseignés</span>
                )}
                <span className="text-slate-400 text-sm group-open:rotate-90 transition-transform">▶</span>
              </div>
            </summary>
            <div className="mt-3 pl-1 space-y-5">
              <p className="text-xs text-slate-500">
                Ces documents sont <strong>obligatoires pour publier vos créneaux</strong> mais peuvent être renseignés plus tard depuis cette page.
              </p>

              <div>
                <div className="flex items-center justify-between mb-2"><Label>Diplômes</Label><Button type="button" variant="outline" size="sm" onClick={addDip}>+ Ajouter</Button></div>
                {form.diplomas.map((d, i) => (
                  <div key={d._uid || i} className="grid grid-cols-3 gap-2 mb-2">
                    <Input placeholder="Nom du diplôme" value={d.name} onChange={(e) => updDip(i, "name", e.target.value)} />
                    <Input type="date" value={d.issued_at} onChange={(e) => updDip(i, "issued_at", e.target.value)} />
                    <Input type="date" value={d.valid_until} onChange={(e) => updDip(i, "valid_until", e.target.value)} />
                  </div>
                ))}
              </div>

              <div>
                <h3 className="font-semibold mb-2 text-slate-900">Attestation d'assurance professionnelle <span className="text-red-500">*</span></h3>
                {insurance.length > 0 ? (
                  insurance.map((i) => (
                    <div key={i.insurance_id} className="card-soft p-3 flex items-center justify-between mb-2" data-testid={`ins-${i.insurance_id}`}>
                      <div>
                        <div className="text-sm font-medium">{i.original_filename}</div>
                        <div className="text-xs text-slate-500">Valide jusqu'au {i.valid_until} {i.is_valid ? <span className="text-emerald-700">• OK</span> : <span className="text-red-600">• Expirée</span>}</div>
                      </div>
                      <Button size="sm" variant="ghost" type="button" onClick={() => delInsurance(i.insurance_id)} data-testid={`del-ins-${i.insurance_id}`}><X className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-amber-700 bg-amber-50 p-2 rounded-lg mb-2">Aucune attestation. Téléversez votre attestation pour pouvoir publier vos créneaux.</div>
                )}
                <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                  <div>
                    <Label>Valide jusqu'au</Label>
                    <Input type="date" value={insForm.valid_until} onChange={(e) => setInsForm({ ...insForm, valid_until: e.target.value })} data-testid="ins-valid" />
                  </div>
                  <div>
                    <Label>Fichier (PDF/JPG/PNG)</Label>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setInsForm({ ...insForm, file: e.target.files[0] })} data-testid="ins-file" />
                  </div>
                  <Button type="button" disabled={insBusy} onClick={submitInsurance} className="rounded-full bg-sky-700 hover:bg-sky-600 h-10" data-testid="ins-upload-btn">
                    {insBusy ? "..." : "Téléverser"}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">En utilisant MySwimmingLessons, vous reconnaissez être un professionnel indépendant responsable de vos prestations, de vos obligations légales, fiscales et d'assurance.</p>
              </div>
            </div>
          </details>

          <label className="flex items-start gap-2 bg-amber-50 p-3 rounded-xl text-sm cursor-pointer">
            <input type="checkbox" checked={form.certified_engagement} onChange={(e) => setForm({ ...form, certified_engagement: e.target.checked })} data-testid="cert-engagement" />
            <span>Je certifie sur l'honneur que mes diplômes sont à jour et en cours de validité, et que je suis bien rattaché(e) à l'établissement déclaré.</span>
          </label>

          <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="save-mns-profile">Enregistrer</Button>
        </form>
      </div>
    </div>
  );
}

function generateInvoicePDF(inv) {
  const doc = new jsPDF();
  doc.setFontSize(18);
  doc.text("FACTURE", 105, 20, { align: "center" });
  doc.setFontSize(10);
  doc.text(`N° ${inv.invoice_number}`, 14, 32);
  doc.text(`Émise le: ${new Date(inv.issued_at).toLocaleDateString("fr-FR")}`, 14, 38);
  doc.text(`Émetteur: ${inv.mns?.name || ""}`, 14, 50);
  doc.text(`Client: ${inv.client?.name || ""}`, 14, 56);
  doc.text(`Piscine: ${inv.pool?.name || ""}`, 14, 62);
  doc.text(`Cours du ${new Date(inv.booking.start_dt).toLocaleString("fr-FR")}`, 14, 76);
  doc.text(`Durée: ${inv.booking.hours}h`, 14, 82);
  doc.text(`Tarif: ${inv.booking.base_amount}€`, 14, 88);
  doc.text(`Commission service (10%): ${inv.booking.commission}€`, 14, 94);
  doc.setFontSize(14);
  doc.text(`TOTAL: ${inv.booking.amount}€`, 14, 108);
  doc.setFontSize(10);
  doc.text(`Mode: ${inv.booking.payment_method === "online" ? "En ligne (Stripe)" : "Espèces"}`, 14, 116);
  doc.text(`Statut: ${inv.booking.payment_status}`, 14, 122);
  doc.save(`${inv.invoice_number}.pdf`);
}

export function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const [bookingIds, setBookingIds] = useState([]);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setBookingIds(data.booking_ids || (data.booking_id ? [data.booking_id] : []));
          toast.success("Réservation confirmée ! Un email de confirmation vous a été envoyé.");
          window.dispatchEvent(new Event("cart:updated"));
        }
        else if (data.status === "expired") setStatus("expired");
        else if (attempts++ < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch { setStatus("error"); }
    };
    poll();
  }, []);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const downloadIcs = (bookingId) => {
    window.location.href = `${apiBase}/api/bookings/${bookingId}/calendar.ics`;
  };
  const downloadAllIcs = () => {
    window.location.href = `${apiBase}/api/bookings/me/calendar.ics`;
  };
  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="card-soft p-8">
        {status === "checking" && <p>Vérification du paiement...</p>}
        {status === "paid" && (
          <>
            <Check className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold mt-3">Réservation confirmée ✅</h1>
            <p className="text-sm text-slate-600 mt-2">
              Votre cours est réservé avec succès.<br />
              Le coach vous contactera si nécessaire.
            </p>

            {/* Iter40 quater: invite the client to sync the lesson(s) with their calendar
                right after payment confirmation. Reminders J-24h + H-1 are embedded. */}
            <div className="mt-5 p-4 rounded-xl bg-sky-50 border border-sky-200 text-left">
              <h3 className="font-semibold text-sky-900 text-sm flex items-center gap-1">
                📅 Ajoutez votre cours à votre agenda
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Compatible Google, Apple, Outlook. Rappels automatiques 24h et 1h avant.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {bookingIds.length === 1 ? (
                  <button
                    type="button"
                    onClick={() => downloadIcs(bookingIds[0])}
                    className="text-sm px-4 py-2 rounded-full bg-sky-700 hover:bg-sky-600 text-white font-medium transition"
                    data-testid="payment-success-ics"
                  >
                    Télécharger le fichier (.ics)
                  </button>
                ) : (
                  <>
                    {bookingIds.map((bid) => (
                      <button
                        key={bid}
                        type="button"
                        onClick={() => downloadIcs(bid)}
                        className="text-sm px-3 py-1.5 rounded-full bg-white border border-sky-300 hover:bg-sky-50 text-sky-700 font-medium transition"
                        data-testid={`payment-success-ics-${bid}`}
                      >
                        Cours #{bid.slice(-6).toUpperCase()}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={downloadAllIcs}
                      className="text-sm px-4 py-2 rounded-full bg-sky-700 hover:bg-sky-600 text-white font-medium transition"
                      data-testid="payment-success-ics-all"
                    >
                      Tous mes cours (.ics)
                    </button>
                  </>
                )}
              </div>
            </div>

            <p className="text-xs text-slate-400 mt-4">Nous vous rappelons que la prestation est réalisée sous la responsabilité du coach.</p>
            <a href="/dashboard" className="inline-block mt-5 text-sm text-sky-700 hover:text-sky-900 font-medium">
              Retour au tableau de bord →
            </a>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && <><AlertOctagon className="w-16 h-16 text-amber-500 mx-auto" /><h1 className="text-2xl font-bold mt-3">Paiement non confirmé</h1><a href="/dashboard" className="inline-block mt-4 text-sky-700 font-medium">Retour au tableau de bord</a></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return <div className="max-w-md mx-auto px-4 py-16 text-center"><div className="card-soft p-8"><h1 className="text-2xl font-bold">Paiement annulé</h1><a href="/dashboard" className="inline-block mt-4 text-sky-700 font-medium">Retour</a></div></div>;
}
